import { useHistory } from 'react-router-dom';
import { useEffect, useState, useCallback, Fragment } from 'react';
import { Button, TextField, InputAdornment, CircularProgress, Fade } from '@mui/material';
import {
  setAccessToken,
  setAccessKey,
  setClientEmail,
  setLoggedIn,
  getExternalAccessStorage,
  previewParam,
} from 'utils/externalAccess';
import { getTokenByKey, get, verifyCredentials, getNA } from 'api/externalAccess';
import styles from './external-access-login.module.scss';
import { getCountry } from 'utils/country';
import { getOwner } from 'api/needsassessments';
import { ReactComponent as MailIcon } from 'icons/message.svg';
import { ReactComponent as PasswordIcon } from 'icons/password.svg';
import information from 'img/information.svg';
import { useTranslation } from 'react-i18next';
import ExternalAccessLayout from 'components/common/ExternalClientLayout/ExternalAccessLayout';
import { useValidator } from 'hooks/useValidator';
import { HtmlTooltip } from 'constants/tooltip';
import { emailRegex } from 'constants/regex';
import { sendEmails } from 'api/emails';
import { getExternalSupportEmailAndNameByCountry } from 'config/supportConfig';
import {
  generateExternalNotificationEmailHtml,
  getExternalNotificationsAttachments,
} from 'utils/emails';
import { epochToISOFormat } from 'utils/date';
import searchParams from 'constants/searchParams';
import { getNotFoundPagePath, getFullPathOrHome } from 'utils/paths';
import paths from 'constants/paths';

const TRIES_MAX_COUNT = 5;

export default function ExternalAccessLogin() {
  const notfound = getNotFoundPagePath();
  const history = useHistory();
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);

  const accessKey = params.get(searchParams.accessKey);
  const paramsEmail = params.get(searchParams.email);
  const bypass = params.get(searchParams.promptBypass);
  const preview = params.get(searchParams.preview);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [externalAccessConfig, setExternalAccessConfig] = useState({});
  const [error, setError] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [tries, setTries] = useState(0);
  const [loading, setLoading] = useState(true);

  const passwordTooltip = (
    <div className={styles.tooltip}>
      <div className={styles.description}>{t('externalAccess.login.passwordTooltip')}</div>
    </div>
  );

  const goToOptionsPage = useCallback(() => {
    return history.push(
      getFullPathOrHome(
        `${paths.externalAccess}?${searchParams.accessKey}=${accessKey}${previewParam(preview)}`,
      ),
    );
  }, [history, accessKey, preview]);

  const verifyCredentialsAndGoToOptionsPage = useCallback(
    async (clientEmail, clientPassword = password) => {
      if (!externalAccessConfig.brokerNaId) return;
      setClientEmail(clientEmail);
      try {
        setLoginLoading(true);
        const response = await verifyCredentials(accessKey, clientPassword, clientEmail);
        setLoginLoading(false);
        if (response) {
          goToOptionsPage();
          setLoggedIn(true);
          setAccessKey(accessKey);

          if (preview !== 'true') {
            const na = await getNA(externalAccessConfig.brokerNaId, accessKey);

            await sendEmails(
              getExternalSupportEmailAndNameByCountry(getCountry()),
              [{ Email: na.owner?.email.toLowerCase(), Name: na.owner?.name }],
              t('externalAccess.emailTemplate.subject'),
              generateExternalNotificationEmailHtml(
                na.owner?.name.split(' ').shift(),
                na.name,
                epochToISOFormat(new Date(), true),
                na.id,
                accessKey,
              ),
              getExternalNotificationsAttachments(),
            );
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          history.push(getFullPathOrHome(paths.externalAccessDenied));
        }
        setTries(tries + 1);
        setError(true);
        setLoginLoading(false);
      }
    },
    [
      history,
      password,
      accessKey,
      tries,
      preview,
      goToOptionsPage,
      externalAccessConfig.brokerNaId,
    ],
  );

  const getExternalAccess = useCallback(async () => {
    if (
      getExternalAccessStorage()?.loggedIn &&
      getExternalAccessStorage()?.accessKey === accessKey
    ) {
      goToOptionsPage();
      return;
    }

    const accessToken = await getTokenByKey(accessKey)
      .then((token) => {
        setAccessToken(token);
        return token;
      })
      .catch(async (e) => {
        if (e.response.status === 404) {
          history.push({
            pathname: notfound,
            state: {
              message: t('notFoundPage.notExistentError'),
            },
          });
        } else if (e.response.status === 403) {
          const owner = await getOwner(accessKey);
          history.push({
            pathname: notfound,
            state: {
              message: t('notFoundPage.forbiddenAccessError', {
                email: owner.email?.toLowerCase(),
              }),
            },
          });
        } else if (e.response.status === 400) {
          history.push(getFullPathOrHome(paths.externalAccessBadRequest));
        } else history.push(notfound);
      });
    if (!accessToken) return;

    const response = await get(accessKey);

    setExternalAccessConfig(response);
    if (paramsEmail) {
      if (!emailRegex.test(paramsEmail)) {
        setLoading(false);
        return;
      }
      setEmail(paramsEmail);
      if (bypass?.toLowerCase() === 'true' && response.passwordEnabled) {
        await verifyCredentialsAndGoToOptionsPage(paramsEmail, response.password);
      } else if (!response.passwordEnabled) {
        await verifyCredentialsAndGoToOptionsPage(paramsEmail);
      }
    }

    setLoading(false);
  }, [
    accessKey,
    history,
    paramsEmail,
    bypass,
    goToOptionsPage,
    verifyCredentialsAndGoToOptionsPage,
  ]);

  useEffect(() => {
    getExternalAccess();
  }, [getExternalAccess]);

  useEffect(() => {
    if (tries === TRIES_MAX_COUNT) setErrorMessage(t('externalAccess.login.maxTriesError'));
    else setErrorMessage(t('externalAccess.login.errorMessage'));
  }, [tries, t]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validator.validate() || loginLoading) return;

    verifyCredentialsAndGoToOptionsPage(email);
  };

  const [validables, validator] = useValidator(['email', 'password'], () => {
    const message = {};
    if (!email || !emailRegex.test(email)) {
      message.email = t('externalAccess.login.errorMessage');
      setError(true);
    } else if (!password && externalAccessConfig.passwordEnabled) {
      message.password = t('externalAccess.login.errorMessage');
      setError(true);
    } else {
      setError(false);
    }
    return message;
  });

  const content = (
    <div className={styles.external_access__container}>
      {loading ? (
        <CircularProgress size={36} />
      ) : (
        <form onSubmit={handleLogin} className={styles.external_access__login_form}>
          <span className={styles.external_access__input_title}>
            {t('externalAccess.login.email')}
          </span>
          <TextField
            inputProps={{ 'data-testid': 'ExternalAccessEmailInput' }}
            required
            className={styles.external_access__input}
            onChange={(event) => setEmail(event.target.value)}
            value={email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailIcon />
                </InputAdornment>
              ),
            }}
            error={!validables.email.valid || error}
            disabled={loginLoading}
          />
          {externalAccessConfig?.passwordEnabled ? (
            <Fragment>
              <div className={styles.external_access__input_title_container}>
                <span className={styles.external_access__input_title}>
                  {t('externalAccess.login.password')}
                </span>
                <HtmlTooltip
                  followCursor
                  title={passwordTooltip}
                  TransitionComponent={Fade}
                  placement="right">
                  <img src={information} alt="" />
                </HtmlTooltip>
              </div>
              <TextField
                inputProps={{ 'data-testid': 'ExternalAccessPasswordInput' }}
                className={styles.external_access__input}
                onChange={(event) => setPassword(event.target.value)}
                value={password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon />
                    </InputAdornment>
                  ),
                }}
                error={!validables.password.valid || error}
                disabled={loginLoading}
              />
            </Fragment>
          ) : null}
          {error ? (
            <span data-testid="ExternalAccessError" className={styles.external_access__error}>
              {errorMessage
                ? errorMessage
                : validables.email.message || validables.password.message}
            </span>
          ) : null}
          <Button
            type="submit"
            data-testid="ExternalAccessLoginButton"
            className={styles.external_access__btn}
            variant="contained"
            onClick={handleLogin}
            disabled={tries === TRIES_MAX_COUNT}>
            {loginLoading ? <CircularProgress size={24} /> : t('externalAccess.login.open')}
          </Button>
        </form>
      )}
    </div>
  );
  return (
    <ExternalAccessLayout
      children={content}
      description={t('externalAccess.login.description')}></ExternalAccessLayout>
  );
}
